import {useCallback} from 'react';

import {User} from '@/domain/models/User';
import {FACEBOOK_PIXEL_ID} from '@/shared/config';
import {hashObject} from '@/shared/lib/object/hashObject';
import {facebookService} from '@/shared/services/analytics/facebook';
import {logger} from '@/shared/services/logger';

let initializing = false;
let lastAppliedUserHash: string;

export function useFacebookPixel() {
  const initPixel = useCallback(() => {
    // already initialized
    if (window.fbq || initializing) {
      return;
    }

    initializing = true;

    ((
      f: any,
      b: Document,
      e: string,
      v: string,
      n?: any,
      t?: HTMLScriptElement,
      s?: HTMLElement
    ) => {
      if (f.fbq) return;
      n = f.fbq = (...args: any[]) => {
        n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n.push || (() => undefined);
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e) as HTMLScriptElement;
      t.async = true;
      t.src = v;
      // @ts-expect-error
      s = b.getElementsByTagName(e)[0];
      // @ts-expect-error
      s.parentNode?.insertBefore(t, s);
    })(
      window,
      document,
      'script',
      'https://connect.facebook.net/en_US/fbevents.js'
    );

    // Initialize the Pixel
    // @ts-expect-error
    window.fbq('init', FACEBOOK_PIXEL_ID);
    // Track page views
    facebookService.track({
      method: 'track',
      event: 'PageView',
    });

    logger.debug('Facebook Pixel initialized');

    initializing = false;
  }, []);

  const setPixelUser = useCallback(
    async (user: {
      email: NonNullable<User['email']>;
      id?: number;
      firstName?: User['first_name'];
      gender?: User['gender'];
      countryCode?: string;
      dob?: User['dob'];
    }) => {
      if (!window.fbq) {
        return;
      }

      const userHash = await hashObject(user);
      if (lastAppliedUserHash === userHash) {
        return;
      }

      lastAppliedUserHash = userHash;

      const payload: {
        em?: string;
        external_id?: string;
        // Using Roman alphabet a-z characters is recommended. Lowercase only with no punctuation
        fn?: string;
        // Use the lowercase, 2-letter country codes in ISO 3166-1 alpha-2
        // Normalized format: us
        country?: string;
        // We accept gender in the form of an initial in lowercase.
        ge?: string;
        // We accept the YYYYMMDD format accommodating a range of month, day and year combinations, with or without punctuation.
        // Input: 2/16/1997, Normalized format: 19970216
        db?: string;
      } = {
        em: user.email,
      };

      if (user.id) {
        payload.external_id = String(user.id);
      }

      if (user.firstName) {
        payload.fn = String(user.firstName).toLowerCase();
      }

      if (user.countryCode) {
        payload.country = String(user.countryCode).toLowerCase();
      }

      if (user.gender) {
        payload.ge = String(user.gender).toLowerCase();
      }

      if (user.dob) {
        const [day, month, year] = user.dob.split('.');
        payload.db = `${year}${month}${day}`;
      }

      // I did not find a way to set the user id in the fbq library, only in init
      window.fbq('init', FACEBOOK_PIXEL_ID, payload);
    },
    []
  );

  return {initPixel, setPixelUser};
}
